import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoiceRecord, Locale } from '@worki/shared';
import { formatDate } from '../lib/format.js';

/** One labelled line of an invoice's details. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-xs font-medium truncate">{children}</dd>
    </div>
  );
}

/**
 * INV-011 — the dates an invoice carries: the one it prints, and under it the
 * day its mail came.
 *
 * The day Worki pulled the row in is deliberately not one of them. It was shown
 * as "received", and beside an invoice mailed on the 3rd it said the 4th — a
 * date that belongs to no document and sent its owner looking for a mail that
 * never came. An invoice that came by no mail has no mail date to show.
 */
export function InvoiceDateRows({ invoice, locale }: { invoice: InvoiceRecord; locale: Locale }) {
  const { t } = useTranslation();
  return (
    <>
      <DetailRow label={t('invoices.issuedAt')}>
        <span className="ltr-nums">{formatDate(invoice.issuedAt, locale)}</span>
      </DetailRow>
      {invoice.receivedAt && (
        <DetailRow label={t('invoices.receivedAt')}>
          <span className="flex flex-col items-end ltr-nums">
            <span>{formatDate(invoice.receivedAt, locale)}</span>
            <time dateTime={invoice.receivedAt} className="text-muted-foreground">
              {new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(invoice.receivedAt))}
            </time>
          </span>
        </DetailRow>
      )}
    </>
  );
}
